import { AppLayout } from '@/components/Layouts';
import { SiteLayout } from '@/components/Layouts/site';
import { AuthContextProvider } from '@/context/AuthContext';
import { UnriddleContextProvider } from '@/context/UnriddleContext';
import { WindowProvider } from '@/context/WindowContext';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import '@/styles/globals.css';
import '@/styles/prosemirror.css';
import { DefaultSeo } from 'next-seo';
import { Router } from 'next/router';

import GoogleAnalytics from '@/components/GoogleAnalytics';
import { SessionProvider } from 'next-auth/react';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';

import { TRPCReactProvider } from '@/lib/trpc/react';
import { Inter } from 'next/font/google';

const inter = Inter({
  subsets: ['latin', 'latin-ext', 'cyrillic', 'greek', 'vietnamese'],
  variable: '--font-inter',
});

import ConfirmationStore from '@/components/Confirmation/ConfirmationStore';
import { Toaster } from '@/components/Sonner';
import { ThemeProvider } from '@/components/theme-provider';
import { TourProvider } from '@/components/tour/TourContext';
import { ModelFallback } from '@/context/ModelFallback';
// pages/_app.tsx
import {
  FingerprintJSPro,
  FpjsProvider,
} from '@fingerprintjs/fingerprintjs-pro-react';
import { LiveblocksProvider } from '@liveblocks/react';
import { env } from 'env';
import { Provider } from 'jotai';
import type { AppProps } from 'next/app';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect } from 'react';

const fpjsPublicApiKey = process.env.NEXT_PUBLIC_FPJS_PUBLIC_API_KEY;
const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;

const pageview = () => {
  try {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    (window as any).fbq?.('track', 'PageView');
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    (window as any).rdt('track', 'PageVisit');
  } catch (error) {
    console.error('Error tracking page view', error);
  }
};

const handleRouteChange = () => {
  pageview();
};

const lightOnlyPages = [
  '/pricing',
  '/login',
  '/signup',
  '/',
  '/changelog',
  '/no-emails',
  '/discount-checkout',
  '/faqs',
  '/payment',
  '/summarize',
  '/affiliate',
  '/blog',
];

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  const forcedTheme =
    lightOnlyPages.includes(router.pathname) ||
    router.pathname.startsWith('/blog') ||
    router.pathname.startsWith('/authors') ||
    router.pathname.startsWith('/papers')
      ? 'light'
      : undefined;

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const isLandingPage =
    router.pathname === '/' ||
    router.pathname === '/summarize' ||
    router.pathname === '/read' ||
    router.pathname === '/pricing' ||
    router.pathname === '/biology' ||
    router.pathname === '/finance' ||
    router.pathname === '/changelog' ||
    router.pathname === '/faqs' ||
    router.pathname === '/login' ||
    router.pathname === '/checkout' ||
    router.pathname === '/affiliate' ||
    router.pathname === '/onboarding' ||
    router.pathname === '/books' ||
    router.pathname === '/no-emails' ||
    router.pathname === '/discount-checkout' ||
    router.pathname.startsWith('/blog') ||
    router.pathname.startsWith('/authors') ||
    router.pathname.startsWith('/papers') ||
    router.pathname.startsWith('/chat-paper') ||
    router.pathname === '/404';

  const isAuthed = router.pathname.startsWith('/settings');

  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      autocapture: false,
      person_profiles: 'identified_only',
      // Enable debug mode in development
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') posthog.debug();
      },
    });

    const handleRouteChange = () => posthog?.capture('$pageview');

    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  const metaDescription =
    'Anara is an AI research assistant that helps you quickly find insights in research papers so you can write literature reviews faster. Join 1M+ academics using Anara to speed up their work.';
  const metaKeywords =
    'ai app, ai tool, free ai, research paper ai, literature review ai, ai writing generator, ai research, writing ai, study ai, ai search engine, ai program, best ai, essay generator free, ai for literature review, best ai writing generator';

  if (router.pathname === '/auth/error' || router.pathname === '/auth/email') {
    return (
      <SessionProvider session={pageProps.session}>
        <style jsx global>{`
        :root {
          --font-inter: ${inter.style.fontFamily};
        }
      `}</style>
        <Component {...pageProps} />
      </SessionProvider>
    );
  }

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange
      storageKey="unriddle-theme"
      forcedTheme={forcedTheme}
    >
      <FpjsProvider
        loadOptions={{
          apiKey: fpjsPublicApiKey as string,
          endpoint: [
            'https://metrics.anara.com',
            FingerprintJSPro.defaultEndpoint,
          ],
          scriptUrlPattern: [
            'https://metrics.anara.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js',
            FingerprintJSPro.defaultScriptUrlPattern,
          ],
        }}
      >
        <NuqsAdapter>
          <Provider>
            <TRPCReactProvider>
              <PostHogProvider client={posthog}>
                <SessionProvider session={pageProps.session}>
                  <LiveblocksProvider authEndpoint="/new-api/liveblocks-auth">
                    <ConfirmationStore.Provider initialValue={null}>
                      <AuthContextProvider isLandingPage={isLandingPage}>
                        <ModelFallback />
                        <UnriddleContextProvider>
                          <WindowProvider>
                            <TourProvider>
                              <DefaultSeo
                                canonical={`${env.NEXT_PUBLIC_SITE_URL.replace(/\/$/, '')}${router.pathname === '/' ? '' : router.pathname}`}
                                description={metaDescription}
                                key={metaKeywords}
                                title="Anara | Faster research"
                                openGraph={{
                                  type: 'website',
                                  title: 'Anara | Faster research',
                                  locale: 'en_US',
                                  url: 'https://anara.com',
                                  images: [
                                    {
                                      url: 'https://anara.com/images/og-main-anara.png',
                                      width: 1200,
                                      height: 630,
                                      alt: 'Anara | Faster research',
                                    },
                                  ],
                                  siteName: 'anara.com',
                                }}
                                twitter={{
                                  handle: '@anaralabs',
                                  site: '@anaralabs',
                                  cardType: 'summary_large_image',
                                }}
                              />
                              <Head>
                                <title>Anara | Faster research</title>
                                <meta
                                  name="description"
                                  content={metaDescription}
                                />
                                <meta name="keywords" content={metaKeywords} />
                                <meta
                                  name="viewport"
                                  content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
                                />
                                {process.env.NEXT_PUBLIC_REACT_SCAN ===
                                  'true' && (
                                  <script
                                    crossOrigin="anonymous"
                                    src="//unpkg.com/react-scan/dist/auto.global.js"
                                  />
                                )}
                              </Head>

                              <GoogleAnalytics />
                              <style jsx global>
                                {`
                            :root {
                              --font-inter: ${inter.style.fontFamily};
                            }
                          `}
                              </style>
                              <Toaster />
                              <Script
                                src="https://analytics.ahrefs.com/analytics.js"
                                data-key="0LigKN5A+twS9UQa1qoMDg"
                                strategy="afterInteractive"
                              />
                              <Script id="facebook-pixel">
                                {`
                                  !function(f,b,e,v,n,t,s)
                                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                                  n.queue=[];t=b.createElement(e);t.async=!0;
                                  t.src=v;s=b.getElementsByTagName(e)[0];
                                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                                  'https://connect.facebook.net/en_US/fbevents.js');
                                  fbq('init', ${FB_PIXEL_ID});
                                  fbq('track', 'PageView');
                                `}
                              </Script>
                              <Script
                                id="reddit-pixel"
                                strategy="afterInteractive"
                              >
                                {`
                                  !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
                                  rdt('init','a2_h2rrtjgqoqel');
                                  rdt('track', 'PageVisit');
                                `}
                              </Script>
                              <Script
                                src="https://r.wdfl.co/rw.js"
                                data-rewardful="888069"
                              />
                              <Script
                                id="rewardful-queue"
                                strategy="beforeInteractive"
                              >
                                {`(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`}
                              </Script>
                              {isLandingPage || isAuthed ? (
                                <Component {...pageProps} />
                              ) : (
                                <>
                                  <Script
                                    async
                                    src="https://cdn.tolt.io/tolt.js"
                                    data-tolt="49b758d8-7ee0-4a4a-952e-b91fbc1dd70d"
                                    strategy="afterInteractive"
                                  />
                                  <Script
                                    id="koala-snippet"
                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                                    dangerouslySetInnerHTML={{
                                      __html: `!function(t){if(window.ko)return;window.ko=[],["identify","track","removeListeners","on","off","qualify","ready"].forEach(function(t){ko[t]=function(){var n=[].slice.call(arguments);return n.unshift(t),ko.push(n),ko}});var n=document.createElement("script");n.async=!0,n.setAttribute("src","https://cdn.getkoala.com/v1/pk_4555280714cebd91564dabc9340abfa89e6e/sdk.js"),(document.body || document.head).appendChild(n)}();`,
                                    }}
                                  />

                                  <SiteLayout>
                                    <AppLayout>
                                      <Component {...pageProps} />
                                    </AppLayout>
                                  </SiteLayout>
                                </>
                              )}
                            </TourProvider>
                          </WindowProvider>
                        </UnriddleContextProvider>
                      </AuthContextProvider>
                    </ConfirmationStore.Provider>
                  </LiveblocksProvider>
                </SessionProvider>
              </PostHogProvider>
            </TRPCReactProvider>
          </Provider>
        </NuqsAdapter>
      </FpjsProvider>
    </ThemeProvider>
  );
}
